import { useMemo } from 'react'
import { filter } from 'lodash-es'
import { useElementLoadStore, useControlStore, useTransmissionStore } from '@editorStores'
import useElementTypes from './useElementTypes'
import useGuidToDomain from './useGuidToDomain'
import useGuidToElement from './useGuidToElement'
import useLoadProblems from './useLoadProblems'
import usePositionGuidToWallGuid from './usePositionGuidToWallGuid'

interface Params {
  selectedElement: string
  selectedOpeningGuid?: string | null
}

const useSelectedElementProblems = ({ selectedElement, selectedOpeningGuid }: Params) => {
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const loadsPerElement = useElementLoadStore(state => state.loads)
  const elementTypes = useElementTypes()
  const positionGuidToWallGuid = usePositionGuidToWallGuid()
  const guidToElement = useGuidToElement() as Record<string, ShapeObject>
  const guidToDomain = useGuidToDomain()

  const allProblems = useLoadProblems(
    verticalTransmissionGraph,
    elementTypes,
    positionGuidToWallGuid,
    guidToDomain,
    loadsPerElement,
    guidToElement,
  )?.problemsOnElements

  return useMemo(() => {
    const problems = filter(allProblems, problem => {
      if (!selectedOpeningGuid) return problem.element_guid === selectedElement

      return (
        problem.element_guid === selectedElement || problem.element_guid === selectedOpeningGuid
      )
    })

    const filteredProblems = filter(problems, problem => {
      if (problem.severity === 'error' && problemViewActiveErrors) return true
      if (problem.severity === 'warning' && problemViewActiveWarnings) return true
      return false
    })

    const hasError = filteredProblems.some(problem => problem.severity === 'error')
    const hasWarning = filteredProblems.some(problem => problem.severity === 'warning')

    return {
      filteredProblems,
      hasError,
      hasWarning,
      isEmpty: filteredProblems.length === 0,
    }
  }, [
    allProblems,
    selectedElement,
    selectedOpeningGuid,
    problemViewActiveErrors,
    problemViewActiveWarnings,
  ])
}

export default useSelectedElementProblems
